import { useState, useEffect, useRef } from "react";
import { Link, useLocation, NavLink } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight, ChevronDown, UserCircle2, LogOut } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { MAIN_NAV } from "@/lib/content";
import { PUBLIC_I18N_ENABLED } from "@/i18n";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useMemberAuth } from "@/context/MemberAuthContext";
import { TABS as ACCOUNT_TABS } from "@/pages/membership/MemberDashboard";

const navKey = (label) => label.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");

function AnnouncementBar() {
  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: async () => (await api.get("/settings")).data,
  });
  if (!settings?.announcement_enabled || !settings?.announcement_text) return null;
  const text = settings.announcement_text;
  const link = settings.announcement_link;
  const inner = (
    <span className="inline-flex items-center gap-1.5">
      {text}
      {link && <ArrowUpRight className="h-3 w-3" />}
    </span>
  );
  return (
    <div data-testid="announcement-bar" className="bg-ptf-accent text-ptf-primary text-center px-4 py-2 text-[0.68rem] font-semibold uppercase tracking-[0.14em]">
      {link ? (
        link.startsWith("http") ? (
          <a href={link} target="_blank" rel="noreferrer" className="hover:underline underline-offset-2">{inner}</a>
        ) : (
          <Link to={link} className="hover:underline underline-offset-2">{inner}</Link>
        )
      ) : (
        inner
      )}
    </div>
  );
}

function Logo({ dark }) {
  return (
    <Link to="/" data-testid="header-logo" className="flex items-center gap-3 shrink-0" aria-label="Planet Trotter Foundation home">
      <img src="/ptf-mark.png" alt="" className="h-10 w-10 md:h-11 md:w-11" />
      <span className="leading-none">
        <span className={`block font-display font-extrabold text-[1.15rem] tracking-tight uppercase ${dark ? "text-ptf-bg" : "text-ptf-primary"}`}>
          Planet Trotter
        </span>
        <span className={`block text-[0.6rem] tracking-[0.34em] uppercase mt-0.5 ${dark ? "text-ptf-bg/70" : "text-ptf-text/55"}`}>
          Foundation
        </span>
      </span>
    </Link>
  );
}

const kebab = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

function DesktopNavItem({ item, dark, currentPath, isOpen, onToggle, onClose }) {
  const { t } = useTranslation();
  const base = `inline-flex items-center gap-1 text-[0.72rem] font-medium uppercase tracking-[0.16em] transition-colors duration-300 ${
    dark ? "text-ptf-bg/85 hover:text-ptf-accent" : "text-ptf-text/75 hover:text-ptf-primary"
  }`;
  if (!item.children) {
    return (
      <NavLink
        to={item.to}
        data-testid={`nav-${item.to.replace("/", "")}`}
        className={({ isActive }) => `${base} ${isActive ? (dark ? "!text-ptf-accent" : "!text-ptf-primary") : ""}`}
      >
        {t(`nav.${navKey(item.label)}`, { defaultValue: item.label })}
      </NavLink>
    );
  }
  const groupActive = item.children.some((c) => currentPath === c.to || currentPath.startsWith(c.to + "/"));
  return (
    <div className="relative">
      <button
        type="button"
        data-testid={`nav-group-${kebab(item.label)}`}
        aria-haspopup="true"
        aria-expanded={isOpen}
        onClick={() => onToggle(item.label)}
        className={`${base} ${groupActive ? (dark ? "!text-ptf-accent" : "!text-ptf-primary") : ""}`}
      >
        {t(`nav.${navKey(item.label)}`, { defaultValue: item.label })} <ChevronDown className={`h-3 w-3 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
            className="absolute left-1/2 -translate-x-1/2 top-full pt-4 z-50"
          >
            <div className="bg-ptf-primary text-ptf-bg min-w-[240px] py-3 shadow-2xl border border-ptf-bg/10">
              {item.children.map((c) => (
                <NavLink
                  key={c.to}
                  to={c.to}
                  onClick={onClose}
                  data-testid={`nav-${c.to.replace("/", "")}`}
                  className={({ isActive }) =>
                    `block px-6 py-2.5 text-[0.68rem] font-medium uppercase tracking-[0.14em] transition-colors hover:text-ptf-accent hover:bg-ptf-bg/5 ${isActive ? "text-ptf-accent" : "text-ptf-bg/80"}`
                  }
                >
                  {t(`nav.${navKey(c.label)}`, { defaultValue: c.label })}
                </NavLink>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function DesktopNav({ dark }) {
  const { pathname } = useLocation();
  const [openLabel, setOpenLabel] = useState(null);
  const navRef = useRef(null);

  useEffect(() => setOpenLabel(null), [pathname]);

  useEffect(() => {
    const onPointerDown = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) setOpenLabel(null);
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpenLabel(null);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  const toggle = (label) => setOpenLabel((prev) => (prev === label ? null : label));
  const close = () => setOpenLabel(null);

  return (
    <nav ref={navRef} className="hidden lg:flex items-center gap-7" aria-label="Primary">
      {MAIN_NAV.map((item) => (
        <DesktopNavItem
          key={item.label}
          item={item}
          dark={dark}
          currentPath={pathname}
          isOpen={openLabel === item.label}
          onToggle={toggle}
          onClose={close}
        />
      ))}
    </nav>
  );
}

function MobileMenu({ open, onClose, member, onLogout }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
      setExpanded(null);
    }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="mobile-menu"
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-[60] bg-ptf-primary text-ptf-bg lg:hidden overflow-y-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
        >
          <div className="flex items-center justify-between px-5 py-5 border-b border-ptf-bg/15">
            <Logo dark />
            <button
              type="button"
              data-testid="mobile-menu-close"
              onClick={onClose}
              className="grid place-items-center h-11 w-11 text-ptf-bg"
              aria-label="Close menu"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <nav className="px-5 py-4 flex flex-col pb-10" aria-label="Mobile">
            {PUBLIC_I18N_ENABLED && (
              <div className="flex justify-end pb-4">
                <LanguageSwitcher dark />
              </div>
            )}
            {MAIN_NAV.map((item) => {
              if (!item.children) {
                return (
                  <Link
                    key={item.label}
                    to={item.to}
                    onClick={onClose}
                    data-testid={`mobile-nav-${item.to.replace("/", "")}`}
                    className="flex items-center min-h-[44px] py-3 border-b border-ptf-bg/10 font-display uppercase text-lg tracking-tight hover:text-ptf-accent transition-colors"
                  >
                    {t(`nav.${navKey(item.label)}`, { defaultValue: item.label })}
                  </Link>
                );
              }
              const isOpen = expanded === item.label;
              return (
                <div key={item.label} className="border-b border-ptf-bg/10">
                  <button
                    type="button"
                    onClick={() => setExpanded(isOpen ? null : item.label)}
                    data-testid={`mobile-nav-group-${kebab(item.label)}`}
                    aria-expanded={isOpen}
                    className="w-full flex items-center justify-between gap-3 min-h-[44px] py-3 font-display uppercase text-lg tracking-tight hover:text-ptf-accent transition-colors"
                  >
                    {t(`nav.${navKey(item.label)}`, { defaultValue: item.label })}
                    <ChevronDown className={`h-4 w-4 shrink-0 transition-transform duration-300 ${isOpen ? "rotate-180 text-ptf-accent" : ""}`} />
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                      >
                        <div className="flex flex-col gap-0.5 pl-4 pb-3">
                          {item.children.map((c) => (
                            <Link
                              key={c.to}
                              to={c.to}
                              onClick={onClose}
                              data-testid={`mobile-nav-${c.to.replace("/", "")}`}
                              className="flex items-center gap-2 min-h-[40px] text-sm uppercase tracking-wide text-ptf-bg/75 hover:text-ptf-accent transition-colors"
                            >
                              <span className="text-ptf-accent/70">›</span> {t(`nav.${navKey(c.label)}`, { defaultValue: c.label })}
                            </Link>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
            {member ? (
              <div className="border-b border-ptf-bg/10">
                <button
                  type="button"
                  onClick={() => setExpanded(expanded === "__account__" ? null : "__account__")}
                  data-testid="mobile-account-group"
                  aria-expanded={expanded === "__account__"}
                  className="w-full flex items-center justify-between gap-3 min-h-[44px] py-3 font-display uppercase text-lg tracking-tight hover:text-ptf-accent transition-colors"
                >
                  <span className="inline-flex items-center gap-2"><UserCircle2 className="h-4 w-4" />{member.name?.split(" ")[0] || "My Account"}</span>
                  <ChevronDown className={`h-4 w-4 shrink-0 transition-transform duration-300 ${expanded === "__account__" ? "rotate-180 text-ptf-accent" : ""}`} />
                </button>
                <AnimatePresence initial={false}>
                  {expanded === "__account__" && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden"
                    >
                      <div className="flex flex-col gap-0.5 pl-4 pb-3">
                        {ACCOUNT_TABS.map((tabItem) => (
                          <Link
                            key={tabItem.key}
                            to={`/account?tab=${tabItem.key}`}
                            onClick={onClose}
                            data-testid={`mobile-account-menu-${tabItem.key}`}
                            className="flex items-center gap-2 min-h-[40px] text-sm uppercase tracking-wide text-ptf-bg/75 hover:text-ptf-accent transition-colors"
                          >
                            <tabItem.icon className="h-3.5 w-3.5" /> {tabItem.label}
                          </Link>
                        ))}
                        <button
                          type="button"
                          onClick={() => { onLogout(); onClose(); }}
                          data-testid="mobile-logout-cta"
                          className="flex items-center gap-2 min-h-[40px] text-sm uppercase tracking-wide text-ptf-bg/75 hover:text-ptf-accent transition-colors"
                        >
                          <LogOut className="h-3.5 w-3.5" /> Log Out
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <Link
                to="/account"
                onClick={onClose}
                data-testid="mobile-membership-cta"
                className="flex items-center gap-2 min-h-[44px] py-3 border-b border-ptf-bg/10 font-display uppercase text-lg tracking-tight hover:text-ptf-accent transition-colors"
              >
                <UserCircle2 className="h-4 w-4" /> {t("header.membership", { defaultValue: "Membership" })}
              </Link>
            )}
            <div className="mt-6 grid grid-cols-2 gap-3">
              <Link
                to="/donate"
                onClick={onClose}
                data-testid="mobile-donate-cta"
                className="inline-flex items-center justify-center min-h-[44px] border border-ptf-bg/40 text-ptf-bg px-3 py-3 text-[0.65rem] font-semibold uppercase tracking-[0.08em] whitespace-nowrap"
              >
                {t("header.donate", { defaultValue: "Donate" })}
              </Link>
              <Link
                to="/partnerships"
                onClick={onClose}
                data-testid="mobile-partner-cta"
                className="inline-flex items-center justify-center min-h-[44px] bg-ptf-accent text-ptf-primary px-3 py-3 text-[0.65rem] font-semibold uppercase tracking-[0.08em] whitespace-nowrap"
              >
                {t("header.partner", { defaultValue: "Partner With Us" })}
              </Link>
            </div>

          </nav>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function AccountMenu({ dark, member, onLogout }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onPointerDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKeyDown = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  const triggerCls = `hidden md:inline-flex items-center gap-1.5 px-3 py-2.5 text-[0.7rem] font-medium uppercase tracking-[0.14em] transition-colors duration-300 ${
    dark ? "text-ptf-bg/85 hover:text-ptf-accent" : "text-ptf-primary/80 hover:text-ptf-secondary"
  }`;

  if (!member) {
    return (
      <Link to="/account" data-testid="header-membership-cta" className={triggerCls}>
        <UserCircle2 className="h-4 w-4" />
        {t("header.membership", { defaultValue: "Membership" })}
      </Link>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-testid="header-membership-cta"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={triggerCls}
      >
        <UserCircle2 className="h-4 w-4" />
        {member.name?.split(" ")[0] || "My Account"}
        <ChevronDown className={`h-3 w-3 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
            className="absolute right-0 top-full pt-3 z-50"
          >
            <div className="bg-ptf-primary text-ptf-bg min-w-[230px] py-3 shadow-2xl border border-ptf-bg/10">
              {ACCOUNT_TABS.map((tabItem) => (
                <Link
                  key={tabItem.key}
                  to={`/account?tab=${tabItem.key}`}
                  onClick={() => setOpen(false)}
                  data-testid={`header-account-menu-${tabItem.key}`}
                  className="flex items-center gap-2.5 px-6 py-2.5 text-[0.68rem] font-medium uppercase tracking-[0.14em] transition-colors hover:text-ptf-accent hover:bg-ptf-bg/5 text-ptf-bg/80"
                >
                  <tabItem.icon className="h-3.5 w-3.5" /> {tabItem.label}
                </Link>
              ))}
              <div className="border-t border-ptf-bg/10 mt-2 pt-2">
                <button
                  type="button"
                  onClick={() => { onLogout(); setOpen(false); }}
                  data-testid="header-logout-cta"
                  className="w-full flex items-center gap-2.5 px-6 py-2.5 text-[0.68rem] font-medium uppercase tracking-[0.14em] transition-colors hover:text-ptf-accent hover:bg-ptf-bg/5 text-ptf-bg/80"
                >
                  <LogOut className="h-3.5 w-3.5" /> Log Out
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Header() {
  const { t } = useTranslation();
  const location = useLocation();
  const { member, logout } = useMemberAuth();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const transparent = !scrolled;
  const dark = transparent; // light text over dark hero band

  return (
    <>
      <header
        data-testid="site-header"
        className="fixed top-0 inset-x-0 z-50"
      >
        <AnnouncementBar />
        <div
          className={`transition-all duration-500 ${
            transparent
              ? "bg-transparent py-6"
              : "bg-ptf-bg/95 backdrop-blur-xl border-b border-ptf-text/10 py-3"
          }`}
        >
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12 flex items-center justify-between gap-6">
          <Logo dark={dark} />
          <DesktopNav dark={dark} />
          <div className="flex items-center gap-3">
            {PUBLIC_I18N_ENABLED && (
              <div className="hidden lg:block">
                <LanguageSwitcher dark={dark} />
              </div>
            )}
            <AccountMenu dark={dark} member={member} onLogout={logout} />
            <Link
              to="/donate"
              data-testid="header-donate-cta"
              className={`hidden md:inline-flex items-center px-5 py-2.5 text-[0.7rem] font-semibold uppercase tracking-[0.16em] border transition-colors duration-300 ${
                dark
                  ? "border-ptf-bg/40 text-ptf-bg hover:bg-ptf-bg hover:text-ptf-primary"
                  : "border-ptf-primary/40 text-ptf-primary hover:bg-ptf-primary hover:text-ptf-bg"
              }`}
            >
              {t("header.donate", { defaultValue: "Donate" })}
            </Link>
            <Link
              to="/partnerships"
              data-testid="header-partner-cta"
              className={`hidden md:inline-flex items-center px-5 py-2.5 text-[0.7rem] font-semibold uppercase tracking-[0.16em] transition-colors duration-300 ${
                dark
                  ? "bg-ptf-accent text-ptf-primary hover:brightness-95"
                  : "bg-ptf-primary text-ptf-bg hover:bg-ptf-secondary"
              }`}
            >
              {t("header.partner", { defaultValue: "Partner With Us" })}
            </Link>
            <button
              type="button"
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              className={`lg:hidden grid place-items-center h-11 w-11 ${dark ? "text-ptf-bg" : "text-ptf-primary"}`}
              aria-label={open ? t("header.menu_close", { defaultValue: "Close menu" }) : t("header.menu_open", { defaultValue: "Open menu" })}
              aria-expanded={open}
            >
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
        </div>
      </header>

      <MobileMenu open={open} onClose={() => setOpen(false)} member={member} onLogout={logout} />
    </>
  );
}
